import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { Radio } from 'transfix-ui/components/Radio'
import { makeStyles, Typography } from 'transfix-ui/core'
import { IFacilityReviewStatuses, IVendor } from 'types/graphqlTypes'
import { brandColors, InlineMessage } from 'transfix-ui'
import { TAction } from '../FacilitySelector'

export type TStep =
  | IFacilityReviewStatuses.merged_to_primary
  | IFacilityReviewStatuses.retained_as_duplicate

export interface IActionsSelectorProps {
  allowCreateVendor?: boolean
  vendors?: Pick<IVendor, 'name' | 'id'>[]
  onChange: (action: TStep | undefined, addVendor: boolean) => void
  defaultAction?: TStep
  defaultAddVendor?: boolean
  disableMergeAction?: boolean
  facilityAction?: TAction
}

const useActionsSelectorStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  radioContainer: {
    background: brandColors.coolGray1,
    padding: theme.spacing(3),
    borderRadius: 12,
  },
  radioContainerSelected: {
    background: brandColors.skyBlue1,
  },
  boldText: {
    fontWeight: 600,
  },
  description: {
    marginLeft: 27,
  },
  vendorMessage: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
}))

export const getVendorAssignedMessage = (vendors: IActionsSelectorProps['vendors']) => {
  const vendorNames = vendors?.map(({ name }) => name).join(', ')

  return (
    <>
      <div>Vendor(s) are assigned to this facility: {vendorNames}</div>
      <div>Use the facility page to add additional vendors.</div>
    </>
  )
}

export const ActionsSelector = ({
  allowCreateVendor = false,
  vendors = [],
  onChange,
  defaultAction = IFacilityReviewStatuses.retained_as_duplicate,
  defaultAddVendor = false,
  disableMergeAction = false,
  facilityAction,
}: IActionsSelectorProps) => {
  const styles = useActionsSelectorStyles()
  const [action, setAction] = useState<TStep | undefined>(defaultAction)
  const [addVendor, setAddVendor] = useState<boolean>(defaultAddVendor)
  const hasVendors = vendors.length > 0
  const vendorInfoMessage = hasVendors
    ? getVendorAssignedMessage(vendors)
    : 'This facility currently does not have any vendors. All shipper facilities will automatically get assigned to this vendor'
  const handleChange = (selectedAction: TStep) => () => {
    setAction(selectedAction)

    if (selectedAction === IFacilityReviewStatuses.retained_as_duplicate) {
      setAddVendor(false)
      onChange(selectedAction, false)
    } else {
      onChange(selectedAction, addVendor)
    }
  }
  const handleVendorChange = () => {
    setAddVendor(!addVendor)
    onChange(action, !addVendor)
  }

  const mergeAndArchiveStyles = classNames(styles.radioContainer, {
    [styles.radioContainerSelected]: action === IFacilityReviewStatuses.merged_to_primary,
  })
  const doNothingElseStyles = classNames(styles.radioContainer, {
    [styles.radioContainerSelected]: action === IFacilityReviewStatuses.retained_as_duplicate,
  })

  useEffect(() => {
    if (facilityAction === 'current') {
      setAction(IFacilityReviewStatuses.retained_as_duplicate)
      setAddVendor(false)
      onChange(IFacilityReviewStatuses.retained_as_duplicate, false)
    } else {
      onChange(action, addVendor)
    }
  }, [facilityAction, action, addVendor, onChange])

  return (
    <div className={styles.container}>
      <div className={mergeAndArchiveStyles}>
        <Radio
          label={
            <Typography className={styles.boldText} variant='body2'>
              Merge and archive unselected address (facility)
            </Typography>
          }
          checked={action === IFacilityReviewStatuses.merged_to_primary}
          onChange={handleChange(IFacilityReviewStatuses.merged_to_primary)}
          disabled={disableMergeAction}
        />
        <div className={styles.description}>
          <Typography variant='body2'>
            Selected address will be marked as the verified primary facility and the unselected
            address will be merged (archived) into it.
          </Typography>
          {allowCreateVendor && (
            <div className={styles.vendorMessage}>
              <Checkbox
                label='Create vendor with EDI location code and/or Shipper facility code'
                disabled={
                  hasVendors ||
                  action === IFacilityReviewStatuses.retained_as_duplicate ||
                  disableMergeAction
                }
                checked={addVendor}
                onChange={handleVendorChange}
              />
              <InlineMessage message={vendorInfoMessage} />
            </div>
          )}
        </div>
      </div>
      <div className={doNothingElseStyles}>
        <Radio
          label={
            <Typography className={styles.boldText} variant='body2'>
              Don&apos;t do anything else
            </Typography>
          }
          checked={action === IFacilityReviewStatuses.retained_as_duplicate}
          onChange={handleChange(IFacilityReviewStatuses.retained_as_duplicate)}
        />
        <Typography variant='body2' className={styles.description}>
          Just confirm the selected address. This address will be used when receiving future
          shipments with the above facility identifiers.
        </Typography>
      </div>
    </div>
  )
}
